import 'leaflet/dist/leaflet.css';
import React, { useState, useEffect } from 'react';
import { message, Modal } from 'antd';
import { api } from './services/api';
import { useConfig } from './context/ConfigContext';
import { getCoordinatesFromGoogle, fetchDirections } from './utils/geocoding';

import DashboardTab from './components/tabs/DashboardTab';
import DeliveriesTab from './components/tabs/DeliveriesTab';
import VehiclesTab from './components/tabs/VehiclesTab';
import OptimizationTab from './components/tabs/OptimizationTab';
import AlgorithmLogicTab from './components/tabs/AlgorithmLogicTab';

import DeliveryModal from './components/modals/DeliveryModal';
import VehicleModal from './components/modals/VehicleModal';
import EditDeliveryModal from './components/modals/EditDeliveryModal';
import EditVehicleModal from './components/modals/EditVehicleModal';
import RouteAnalysisModal from './components/modals/RouteAnalysisModal';

const RouteOptimizationSystem = () => {
  const { config } = useConfig();
  const geocodeWithKey = (address, logCallback) =>
    getCoordinatesFromGoogle(address, logCallback, config.googleMapsKey);

  // ── Tab ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('dashboard');

  // ── Deliveries ─────────────────────────────────────────────────────────
  const [deliveries, setDeliveries] = useState([
    { id: 1, name: 'Customer A - Priya Distributors', location: 'Sector 15, Gandhinagar', priority: 'high', time: '9:00 AM', weight: 150, coords: [72.6372917, 23.2355469], timeWindow: { start: 9, end: 11 } },
    { id: 2, name: 'Customer B - Royal Enterprises', location: 'S.G. Highway, Ahmedabad', priority: 'medium', time: '10:30 AM', weight: 200, coords: [72.5200008, 23.0582594], timeWindow: { start: 10, end: 13 } },
    { id: 3, name: 'Customer C - Mehta & Sons', location: 'Vastrapur, Ahmedabad', priority: 'high', time: '11:00 AM', weight: 180, coords: [72.5277849, 23.0376238], timeWindow: { start: 11, end: 14 } },
    { id: 4, name: 'Customer D - City Traders', location: 'Satellite, Ahmedabad', priority: 'low', time: '2:00 PM', weight: 120, coords: [72.5178289, 23.0301227], timeWindow: { start: 14, end: 17 } },
    { id: 5, name: 'Customer E - Patel Wholesale', location: 'Maninagar, Ahmedabad', priority: 'medium', time: '3:30 PM', weight: 160, coords: [72.604097, 22.995165], timeWindow: { start: 15, end: 18 } }
  ]);

  // ── Vehicles ───────────────────────────────────────────────────────────
  const [vehicles, setVehicles] = useState([
    { id: 1, name: 'Vehicle 001', type: 'truck', driver: 'Ramesh Kumar', capacity: 500, location: 'Aslali, Ahmedabad', status: 'available', coords: [72.5970569, 22.9146173] },
    { id: 2, name: 'Vehicle 002', type: 'car', driver: 'Suresh Patel', capacity: 300, location: 'Aslali, Ahmedabad', status: 'available', coords: [72.5970569, 22.9146173] },
    { id: 3, name: 'Vehicle 003', type: 'bike', driver: 'Mahesh Shah', capacity: 100, location: 'Aslali, Ahmedabad', status: 'available', coords: [72.5970569, 22.9146173] }
  ]);

  // ── Optimization ───────────────────────────────────────────────────────
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationStep, setOptimizationStep] = useState(0);
  const [optimizedRoutes, setOptimizedRoutes] = useState([]);
  const [showOptimizedRoutes, setShowOptimizedRoutes] = useState(false);
  const [apiError, setApiError] = useState('');
  const [metrics, setMetrics] = useState({
    totalDistance: '77 km', totalTime: '4.3 hrs', totalCost: '₹770', vehiclesUsed: '2/3', optimization: '33% resource optimization'
  });

  // ── Modals ────────────────────────────────────────────────────────────
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showRouteAnalysisModal, setShowRouteAnalysisModal] = useState(false);
  const [showEditDeliveryModal, setShowEditDeliveryModal] = useState(false);
  const [showEditVehicleModal, setShowEditVehicleModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [instructions, setInstructions] = useState([]);
  const [loadingInstructions, setLoadingInstructions] = useState(false);
  const [directionsError, setDirectionsError] = useState('');

  // ── Algorithm config ──────────────────────────────────────────────────
  const [vehicleTypes, setVehicleTypes] = useState(['bike', 'car', 'truck']);
  const [priorityConfig, setPriorityConfig] = useState(['bike', 'car', 'truck']);
  const [algorithmParams, setAlgorithmParams] = useState({
    maxDistance: 500, maxTime: 8, maxStops: 20, costPerKM: 10, fuelEfficiency: 15,
    trafficFactor: 1.2, timeWindowFlexibility: 0.5, graphHopperProfile: 'car', osmWeighting: 'fastest'
  });

  // ── Spin CSS injected once ────────────────────────────────────────────
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      .custom-tooltip { background-color: rgba(0,0,0,0.85); color: white; border: 1px solid #fff; border-radius: 6px; font-size: 14px; padding: 10px; max-width: 300px; line-height: 1.4; }
      .custom-tooltip::before { border-bottom-color: rgba(0,0,0,0.85); }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // ── Fetch directions when a route is selected ─────────────────────────
  useEffect(() => {
    if (!selectedRoute) return;
    setLoadingInstructions(true);
    const points = [
      { latitude: selectedRoute.vehicle.coords[1], longitude: selectedRoute.vehicle.coords[0] },
      ...selectedRoute.deliveries.map(d => ({ latitude: d.coords[1], longitude: d.coords[0] })),
      { latitude: selectedRoute.vehicle.coords[1], longitude: selectedRoute.vehicle.coords[0] },
    ];
    setDirectionsError('');
    fetchDirections(points)
      .then(ins => { setInstructions(ins); setLoadingInstructions(false); })
      .catch(err => { setInstructions([]); setDirectionsError(err.message); setLoadingInstructions(false); });
  }, [selectedRoute]);

  // ── Helpers ───────────────────────────────────────────────────────────
  const resetOptimization = () => {
    setOptimizationStep(0);
    setShowOptimizedRoutes(false);
    setOptimizedRoutes([]);
    setApiError('');
  };

  const updateMetricsFromAPI = (res) => {
    setMetrics({
      totalDistance: `${res.metrics.totalDistance.toFixed(2)} km`,
      totalTime: `${Math.floor(res.metrics.totalTime)} hrs ${Math.round((res.metrics.totalTime % 1) * 60)} mins`,
      totalCost: `₹${res.metrics.totalCost}`,
      vehiclesUsed: `${res.metrics.vehiclesUsed}/${res.metrics.totalVehicles}`,
      optimization: `↓ ${res.metrics.resourceOptimization}% resource optimization`,
    });
  };

  const transformAPIRoutes = (res) =>
    res.routes.map(route => ({
      vehicle: { id: route.vehicle.id, name: route.vehicle.name, type: route.vehicle.type.toLowerCase(), driver: route.vehicle.driver || 'Driver', capacity: route.vehicle.capacity || 500, coords: [route.vehicle.longitude, route.vehicle.latitude], location: route.vehicle.location },
      deliveries: route.deliveries.map(d => ({ id: d.id, name: d.name, location: d.location, priority: d.priority || 'medium', weight: d.weight || 100, coords: [d.longitude, d.latitude], timeWindow: d.timeWindow || { start: 9, end: 17 } })),
      totalWeight: route.totalWeight || 0, totalDistance: route.totalDistance || 0, totalTime: route.totalTime || 0,
      totalTimeMinutes: route.totalTimeMinutes || 0, totalCost: route.totalCost || 0, routeSequence: route.routeSequence || [],
    }));

  // ── Optimization ──────────────────────────────────────────────────────
  const startOptimization = async () => {
    const available = vehicles.filter(v => v.status === 'available');
    if (available.length === 0) { message.warning('Please make at least one vehicle available.'); return; }

    setIsOptimizing(true);
    setOptimizationStep(0);
    setShowOptimizedRoutes(false);
    setApiError('');

    const interval = setInterval(() => setOptimizationStep(prev => prev < 5 ? prev + 1 : prev), 1000);

    try {
      const payload = {
        Deliveries: deliveries.map(d => ({ Id: d.id, Name: d.name, Location: d.location, Priority: d.priority, Weight: d.weight, Latitude: d.coords[1], Longitude: d.coords[0], TimeWindow: { Start: d.timeWindow.start, End: d.timeWindow.end } })),
        Vehicles: vehicles.map(v => ({ Id: v.id, Name: v.name, Type: v.type.toLowerCase(), Driver: v.driver, Capacity: v.capacity, Location: v.location, Status: v.status, Latitude: v.coords[1], Longitude: v.coords[0], Profile: algorithmParams.graphHopperProfile })),
        VehiclePriority: priorityConfig,
        AllowedVehicleTypes: vehicleTypes,
        AlgorithmParams: algorithmParams
      };

      const res = (await api.post('/api/RouteOptimization/optimize', payload)).data;
      clearInterval(interval);
      setOptimizationStep(5);
      setOptimizedRoutes(transformAPIRoutes(res));
      updateMetricsFromAPI(res);
      setTimeout(() => { setShowOptimizedRoutes(true); setIsOptimizing(false); }, 500);
    } catch (error) {
      clearInterval(interval);
      setIsOptimizing(false);
      setOptimizationStep(0);
      const msg = error?.response?.data?.error || error?.response?.data?.message;
      setApiError(`Optimization failed: ${msg || error.message}`);
    }
  };

  // ── Delivery handlers ─────────────────────────────────────────────────
  const addDelivery = (form) => {
    setDeliveries([...deliveries, { id: deliveries.length > 0 ? Math.max(...deliveries.map(d => d.id)) + 1 : 1, name: form.name, location: form.location, priority: form.priority, time: `${form.timeStart}:00`, weight: form.weight, coords: [form.longitude, form.latitude], timeWindow: { start: form.timeStart, end: form.timeEnd } }]);
    setShowDeliveryModal(false);
    resetOptimization();
  };

  const removeDelivery = (id) => {
    Modal.confirm({ title: 'Remove Delivery', content: 'Are you sure?', okText: 'Yes, Remove', okType: 'danger', cancelText: 'Cancel', onOk: () => { setDeliveries(deliveries.filter(d => d.id !== id)); resetOptimization(); } });
  };

  const editDelivery = (delivery) => { setEditingDelivery(delivery); setShowEditDeliveryModal(true); };

  const updateDelivery = (updated) => {
    setDeliveries(deliveries.map(d => d.id === updated.id ? { ...d, name: updated.name, location: updated.location, weight: updated.weight, priority: updated.priority, timeWindow: { start: updated.timeStart, end: updated.timeEnd }, coords: [updated.longitude, updated.latitude] } : d));
    setShowEditDeliveryModal(false);
    resetOptimization();
  };

  // ── Vehicle handlers ──────────────────────────────────────────────────
  const addVehicle = (form) => {
    setVehicles([...vehicles, { id: vehicles.length > 0 ? Math.max(...vehicles.map(v => v.id)) + 1 : 1, name: form.name, type: form.type.toLowerCase(), driver: form.driver, capacity: form.capacity, location: form.location, status: 'available', coords: [form.longitude, form.latitude] }]);
    setShowVehicleModal(false);
    resetOptimization();
  };

  const removeVehicle = (id) => {
    if (vehicles.length <= 1) { message.warning('Cannot remove the last vehicle!'); return; }
    Modal.confirm({ title: 'Remove Vehicle', content: 'Are you sure?', okText: 'Yes, Remove', okType: 'danger', cancelText: 'Cancel', onOk: () => { setVehicles(vehicles.filter(v => v.id !== id)); resetOptimization(); } });
  };

  const toggleVehicle = (id) => { setVehicles(vehicles.map(v => v.id === id ? { ...v, status: v.status === 'available' ? 'unavailable' : 'available' } : v)); resetOptimization(); };

  const editVehicle = (vehicle) => { setEditingVehicle(vehicle); setShowEditVehicleModal(true); };

  const updateVehicle = (updated) => {
    setVehicles(vehicles.map(v => v.id === updated.id ? { ...v, name: updated.name, type: updated.type, driver: updated.driver, capacity: updated.capacity, location: updated.location, coords: [updated.longitude, updated.latitude] } : v));
    setShowEditVehicleModal(false);
    resetOptimization();
  };

  // ── Algorithm handlers ────────────────────────────────────────────────
  const handleSaveAlgorithmConfig = (cfg) => { setPriorityConfig(cfg.priority); setVehicleTypes(cfg.allowedTypes); setAlgorithmParams(cfg.params); message.success('Algorithm configuration saved!'); };
  const handleResetAlgorithmConfig = () => { setPriorityConfig(['bike', 'car', 'truck']); setVehicleTypes(['bike', 'car', 'truck']); setAlgorithmParams({ maxDistance: 500, maxTime: 8, maxStops: 20, costPerKM: 10, fuelEfficiency: 15, trafficFactor: 1.2, timeWindowFlexibility: 0.5, graphHopperProfile: 'car', osmWeighting: 'fastest' }); };

  // ── Warning ───────────────────────────────────────────────────────────
  const available = vehicles.filter(v => v.status === 'available');
  const totalCapacity = available.reduce((s, v) => s + v.capacity, 0);
  const totalWeight = deliveries.reduce((s, d) => s + d.weight, 0);
  let warningMessage = '';
  if (available.length === 0) warningMessage = 'No vehicles available! Please make at least one vehicle available.';
  else if (totalWeight > totalCapacity) warningMessage = `Warning: Total delivery weight (${totalWeight}kg) exceeds available capacity (${totalCapacity}kg).`;
  else if (available.length < vehicles.length) warningMessage = `${vehicles.length - available.length} vehicle(s) unavailable. Workload redistributed among ${available.length} vehicle(s).`;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", background: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '20px 40px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="3" width="15" height="13"></rect>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
              <circle cx="5.5" cy="18.5" r="2.5"></circle>
              <circle cx="18.5" cy="18.5" r="2.5"></circle>
            </svg>
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: 600, margin: 0 }}>Route Optimization System</h1>
              <p style={{ fontSize: '14px', opacity: 0.9, margin: 0 }}>Intelligent Delivery Management - API Integrated</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={() => { localStorage.removeItem('isAuthenticated'); localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/'; }}
              style={{ padding: '8px 16px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>
              Logout
            </button>
            <span style={{ fontSize: '12px', padding: '4px 10px', background: 'rgba(255,255,255,0.2)', borderRadius: '12px' }}>
              {apiError ? '❌ API Error' : '🟢 API Ready'}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{ background: 'white', borderBottom: '2px solid #e5e7eb', padding: '0 40px' }}>
        <div style={{ display: 'flex', gap: '5px', maxWidth: '1400px', margin: '0 auto' }}>
          {[
            { id: 'dashboard',    label: '📊 Dashboard' },
            { id: 'deliveries',   label: '📦 Deliveries' },
            { id: 'vehicles',     label: '🚚 Vehicles' },
            { id: 'optimization', label: '🎯 Optimization' },
            { id: 'algorithm',    label: '⚙️ Algorithm Logic' }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '15px 25px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '15px', color: activeTab === tab.id ? '#667eea' : '#6b7280', borderBottom: activeTab === tab.id ? '3px solid #667eea' : '3px solid transparent', fontWeight: activeTab === tab.id ? 600 : 400 }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 40px' }}>
        {apiError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <div><strong>Error:</strong> {apiError}<br /><small>Internal Server Error. Please try again later.</small></div>
          </div>
        )}

        {activeTab === 'dashboard'    && <DashboardTab metrics={metrics} optimizedRoutes={optimizedRoutes} />}
        {activeTab === 'deliveries'   && <DeliveriesTab deliveries={deliveries} onAddDelivery={() => setShowDeliveryModal(true)} onRemoveDelivery={removeDelivery} onEditDelivery={editDelivery} />}
        {activeTab === 'vehicles'     && <VehiclesTab vehicles={vehicles} onAddVehicle={() => setShowVehicleModal(true)} onRemoveVehicle={removeVehicle} onToggleVehicle={toggleVehicle} onEditVehicle={editVehicle} />}
        {activeTab === 'optimization' && <OptimizationTab warningMessage={warningMessage} optimizationStep={optimizationStep} isOptimizing={isOptimizing} showOptimizedRoutes={showOptimizedRoutes} optimizedRoutes={optimizedRoutes} onStartOptimization={startOptimization} onResetOptimization={resetOptimization} onViewRouteDetails={(route) => { setSelectedRoute(route); setShowRouteAnalysisModal(true); }} />}
        {activeTab === 'algorithm'    && <AlgorithmLogicTab vehicleTypes={vehicleTypes} priorityConfig={priorityConfig} algorithmParams={algorithmParams} onSaveConfig={handleSaveAlgorithmConfig} onResetConfig={handleResetAlgorithmConfig} />}
      </div>

      {/* Modals */}
      {showDeliveryModal    && <DeliveryModal onClose={() => setShowDeliveryModal(false)} onSubmit={addDelivery} getCoordinates={geocodeWithKey} />}
      {showVehicleModal     && <VehicleModal onClose={() => setShowVehicleModal(false)} onSubmit={addVehicle} getCoordinates={geocodeWithKey} />}
      {showRouteAnalysisModal && <RouteAnalysisModal route={selectedRoute} instructions={instructions} loading={loadingInstructions} directionsError={directionsError} onClose={() => setShowRouteAnalysisModal(false)} />}
      {showEditDeliveryModal && editingDelivery && <EditDeliveryModal delivery={editingDelivery} onClose={() => setShowEditDeliveryModal(false)} onSubmit={updateDelivery} getCoordinates={geocodeWithKey} />}
      {showEditVehicleModal  && editingVehicle  && <EditVehicleModal vehicle={editingVehicle} onClose={() => setShowEditVehicleModal(false)} onSubmit={updateVehicle} getCoordinates={geocodeWithKey} />}
    </div>
  );
};

export default RouteOptimizationSystem;
